import * as React from 'react';

import { cn } from '@/ux-common/lib/utils';

/**
 * Typography component — minimal polymorphic wrapper for text elements.
 *
 * All visual styling is applied via CSS classes. This wrapper provides:
 * - Polymorphic element selection via the `as` prop (default: `'p'`)
 * - `data-component="Typography"` for component identification (consumer can override)
 * - Ref forwarding to the underlying chosen element, typed per element
 * - `className` merging via `cn()`
 * - Full HTML attribute passthrough (id, aria-*, role, onClick, etc.) typed per element
 *
 * Typography ships ALL text-styling classes:
 * - Body / caption: `.text-body`, `.text-caption`
 * - Headings: `.text-page-header`, `.text-category-header`,
 *   `.text-sub-header`, `.text-card-header`, `.text-nav-header`
 *
 * For heading-tier text inside a layout, compose Typography inside
 * `<Header>` — Header provides the flex shell, Typography supplies
 * both the element (`as="h1"` etc.) and the visual style.
 *
 * NOTE: `as` overrides the rendered element but NOT the semantic role.
 * For true headings, pass `as="h1"` etc. — do not use `as="div"` for
 * heading-styled content (screen readers lose the heading landmark).
 *
 * @example
 * ```tsx
 * // Page heading composed with Header (replaces legacy variant="h1")
 * <Header>
 *   <Typography as="h1" className="header-title text-page-header">Trends</Typography>
 * </Header>
 *
 * // Body paragraph (replaces legacy variant="body" or no variant)
 * <Typography as="p" className="text-body">
 *   Compressor discharge pressure exceeded the alarm threshold.
 * </Typography>
 *
 * // Inline caption (replaces legacy variant="h5")
 * <Typography as="span" className="text-caption">Last 30 days</Typography>
 *
 * // Custom element with native typing — `href` is typed against <a>
 * <Typography as="a" href="/docs" className="text-body underline">
 *   Read the docs
 * </Typography>
 * ```
 */

type TypographyOwnProps<T extends React.ElementType> = {
  as?: T;
  className?: string;
  children?: React.ReactNode;
};

export type TypographyProps<T extends React.ElementType = 'p'> = TypographyOwnProps<T> &
  Omit<React.ComponentPropsWithoutRef<T>, keyof TypographyOwnProps<T>>;

type PolymorphicRef<T extends React.ElementType> = React.ComponentPropsWithRef<T>['ref'];

type TypographyComponent = <T extends React.ElementType = 'p'>(
  props: TypographyProps<T> & { ref?: PolymorphicRef<T> }
) => React.ReactElement | null;

export const Typography: TypographyComponent = React.forwardRef(
  <T extends React.ElementType = 'p'>(
    { as, className, children, ...props }: TypographyProps<T>,
    ref: React.Ref<Element>
  ) => {
    const Component = (as ?? 'p') as React.ElementType;
    return (
      <Component ref={ref} data-component="Typography" className={cn(className)} {...props}>
        {children}
      </Component>
    );
  }
) as TypographyComponent;

(Typography as React.FC).displayName = 'Typography';
